import { useEffect, useState } from 'react';

export type Direction = 'left' | 'right';
export type Action = 'in' | 'out';

type UseImageControls = {
  rotationLevel: number;
  zoomLevel: number;
  panPosition: { dx: number; dy: number };
  rotate: (direction: Direction) => void;
  zoom: (action: Action) => void;
  setZoomLevel: (zoom: number) => void;
  setPanPosition: (dx: number, dy: number) => void;
  handleImageLoad: (containerWidth: number, containerHeight: number, imgWidth: number, imgHeight: number) => void;
};

export const maxZoomLevel = 2.5;
export const minZoomLevel = 0.25;

export function useImageControls(selectedFileIndex: number): UseImageControls {
  const [rotationLevel, setRotationLevel] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPositionState] = useState({ dx: 0, dy: 0 });
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const rotate = (direction: Direction) => {
    const nextRotations = {
      left: rotationLevel === -3 ? 0 : rotationLevel - 1,
      right: rotationLevel === 0 ? -3 : rotationLevel + 1,
    };
    setRotationLevel(nextRotations[direction]);
  };

  const zoom = (action: Action) => {
    const nextZoom = {
      in: Math.min(maxZoomLevel, zoomLevel + 0.25),
      out: Math.max(minZoomLevel, zoomLevel - 0.25),
    };
    setZoomLevel(nextZoom[action]);
  };

  const setPanPosition = (dx: number, dy: number) => {
    setPanPositionState({ dx, dy });
  };

  const handleImageLoad = (containerWidth: number, containerHeight: number, imgWidth: number, imgHeight: number) => {
    if (isImageLoaded) {
      return;
    }

    const zoomX = containerWidth / imgWidth;
    const zoomY = containerHeight / imgHeight;

    const newZoom = Math.min(zoomX, zoomY);

    setZoomLevel(newZoom);
    setPanPositionState({
      dx: (containerWidth - imgWidth) / 2,
      dy: (containerHeight - imgHeight) / 2,
    });
    setIsImageLoaded(true);
  };

  useEffect(() => {
    setRotationLevel(0);
    setZoomLevel(1);
    setPanPositionState({ dx: 0, dy: 0 });
    setIsImageLoaded(false);
  }, [selectedFileIndex]);

  return {
    rotationLevel,
    zoomLevel,
    panPosition,
    rotate,
    zoom,
    setZoomLevel,
    setPanPosition,
    handleImageLoad,
  };
}
